// NIGHT-DIRECTSTAFF-2026-10-19 §5: saved views on the Leads list -- "Save this view" turns the current filters, search
// and sort into a named chip ("PT · CO · this month"), per staff account; one tap applies it; … renames or deletes.
//
// STORED per account in public.settings (key "crm.leadViews.v1.<auth user id>", JSON), the table the SMS templates
// already use -- no migration. Allowlisted staff can read the settings table, so a view is not private, just personal.
// DATES: a range that matches "this month", "the last 7 days" or "the last 30 days" when saved is kept RELATIVE, so the
// view still means this month next month; any other range is kept as the dates chosen.

export const MAX_VIEWS = 20;
export const viewsKey = (userId) => `crm.leadViews.v1.${String(userId || 'unknown')}`;
export const SORTS = { newest: 'Newest first', oldest: 'Oldest first', name: 'Name A–Z' };

const pad = (n) => String(n).padStart(2, '0');
const ymd = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };

/** 'this_month' | 'last_7' | 'last_30' | null for a from/to pair on `today`. An empty `to` means "up to now". */
export function relativeRange(from, to, today = new Date()) {
  if (!from || (to && to !== ymd(today))) return null;
  if (from === ymd(new Date(today.getFullYear(), today.getMonth(), 1))) return 'this_month';
  if (from === ymd(addDays(today, -6))) return 'last_7';
  if (from === ymd(addDays(today, -29))) return 'last_30';
  return null;
}
/** The dates a relative range means today -> { from, to: '' } */
export function resolveRange(rel, today = new Date()) {
  if (rel === 'this_month') return { from: ymd(new Date(today.getFullYear(), today.getMonth(), 1)), to: '' };
  if (rel === 'last_7') return { from: ymd(addDays(today, -6)), to: '' };
  if (rel === 'last_30') return { from: ymd(addDays(today, -29)), to: '' };
  return null;
}
const REL_WORDS = { this_month: 'this month', last_7: 'last 7 days', last_30: 'last 30 days' };

/** What a view keeps: the list's state now. `today` decides whether the dates are kept relative. */
export function snapshot({ filters, search, formType, source, handledFilter, onlyNeedsAttention, spamOnly, query, sort }, today = new Date()) {
  const rel = relativeRange(filters && filters.from, filters && filters.to, today);
  return { filters: { ...filters, ...(rel ? { from: '', to: '' } : {}) }, rel, search: { ...search }, formType, source, handledFilter,
    onlyNeedsAttention: !!onlyNeedsAttention, spamOnly: !!spamOnly, query: String(query || ''), sort: SORTS[sort] ? sort : 'newest' };
}
/** The state to apply for a saved view, today. */
export function applied(view, today = new Date()) {
  const dates = view.rel ? resolveRange(view.rel, today) : null;
  return { ...view, filters: { ...view.filters, ...(dates || {}) } };
}

/** A short name from what's chosen: "PT · CO · this month", "Facilities · Meta ad", "“denver” · Oldest first". */
export function suggestName(v, { sourceLabels = {} } = {}) {
  const f = v.filters || {}; const q = v.search || {};
  const parts = [];
  if (f.kind === 'facilities') parts.push('Facilities');
  if (f.discipline && f.discipline !== 'Any') parts.push(f.discipline);
  else if (q.discipline && q.discipline !== 'Any') parts.push(q.discipline);
  if (q.state && q.state !== 'Any') parts.push(q.state);
  if (f.source && f.source !== 'Any') parts.push(sourceLabels[f.source] || f.source);
  if (v.query) parts.push(`“${v.query.trim()}”`);
  if (v.rel) parts.push(REL_WORDS[v.rel]);
  else if (f.from || f.to) parts.push([f.from, f.to].filter(Boolean).join(' to '));
  if (v.onlyNeedsAttention) parts.push('needs attention');
  if (v.spamOnly) parts.push('spam');
  if (v.sort && v.sort !== 'newest') parts.push(SORTS[v.sort]);
  return (parts.join(' · ') || 'All leads').slice(0, 60);
}

/** The saved list, tolerant of anything malformed (a bad entry is dropped, never thrown). */
export function parseViews(raw) {
  let list = [];
  try { list = JSON.parse(raw || '[]'); } catch { list = []; }
  return (Array.isArray(list) ? list : []).filter((v) => v && typeof v.id === 'string' && typeof v.name === 'string' && v.state && typeof v.state === 'object')
    .slice(0, MAX_VIEWS);
}
export const serializeViews = (list) => JSON.stringify((list || []).slice(0, MAX_VIEWS));

const strip = (v) => JSON.stringify({ ...v, filters: { ...(v.filters || {}) } });
/** Is the list showing exactly this view right now? */
export function isActive(view, current) {
  return !!view && !!current && strip(view.state) === strip(current);
}

/** Sort the shown rows. */
export function sortRows(rows, sort) {
  const t = (l) => new Date(l.created_at).getTime() || 0;
  const xs = [...(rows || [])];
  if (sort === 'oldest') return xs.sort((a, b) => t(a) - t(b));
  if (sort === 'name') return xs.sort((a, b) => String(a.name || '~').localeCompare(String(b.name || '~')));
  return xs.sort((a, b) => t(b) - t(a));
}

/** Load / save over the settings table for one staff account. */
export function makeViewsApi({ getSetting, saveSettingChecked, userId }) {
  const key = viewsKey(userId);
  return {
    key,
    load: async () => parseViews(await getSetting(key)),
    save: async (list) => saveSettingChecked(key, serializeViews(list)),
  };
}
export const newViewId = () => `v${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;
